import { useEffect, useRef } from 'react';
import gsap from 'gsap';

const STRIPS = 10;

const PageLoader = ({ onComplete }) => {
  const wrapperRef = useRef(null);   // ← ref on outer wrapper so we can hide it at end
  const stripsRef = useRef([]);
  const counterRef = useRef(null);
  const logoRef = useRef(null);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    const tl = gsap.timeline({
      onComplete: () => {
        // Hide the wrapper completely so it no longer blocks pointer events
        if (wrapperRef.current) wrapperRef.current.style.display = 'none';
        onCompleteRef.current?.();
      },
    });
    const counter = { val: 0 };

    tl
      .fromTo(counterRef.current,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.22, ease: 'none' }
      )
      .to(counter, {
        val: 100,
        duration: 1.55,
        ease: 'power2.inOut',
        onUpdate: () => {
          if (counterRef.current) {
            counterRef.current.textContent = String(Math.round(counter.val)).padStart(3, '0');
          }
        },
      }, 0)
      .fromTo(logoRef.current,
        { autoAlpha: 0, y: 18 },
        { autoAlpha: 0.85, y: 0, duration: 0.4, ease: 'power2.out' },
        '-=0.48'
      )
      .to({}, { duration: 0.28 })
      .to([counterRef.current, logoRef.current], {
        autoAlpha: 0, y: -30,
        duration: 0.38, stagger: 0.05, ease: 'power2.in',
      })
      // Venetian blind: top + bottom strips peel first, converge to centre
      .to(stripsRef.current, {
        yPercent: -105,
        duration: 0.72,
        ease: 'power3.inOut',
        stagger: { amount: 0.44, from: 'edges' },
      }, '-=0.12');

    return () => tl.kill();
  }, []);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        pointerEvents: 'all',
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: STRIPS }, (_, i) => (
        <div
          key={i}
          ref={(el) => (stripsRef.current[i] = el)}
          style={{
            position: 'absolute',
            left: 0, right: 0,
            height: `${100 / STRIPS}%`,
            top: `${i * (100 / STRIPS)}%`,
            background: i % 2 === 0 ? '#002B5B' : '#003875',
            willChange: 'transform',
          }}
        />
      ))}

      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        zIndex: 10, pointerEvents: 'none',
      }}>
        <p
          ref={counterRef}
          style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: 'clamp(72px, 15vw, 144px)',
            fontWeight: 800, color: '#159895',
            margin: 0, lineHeight: 1, letterSpacing: '-0.05em',
            visibility: 'hidden',
          }}
        >000</p>
        <p
          ref={logoRef}
          style={{
            fontFamily: 'Pacifico, cursive',
            fontSize: 'clamp(20px, 2.5vw, 30px)',
            color: 'rgba(255,255,255,0.78)',
            margin: '16px 0 0',
            visibility: 'hidden',
          }}
        >
          Resume.<span style={{ color: '#159895' }}>io</span>
        </p>
      </div>
    </div>
  );
};

export default PageLoader;
